import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Api } from '../lib/api.js';
import { useApi, useWallet } from '../lib/wallet.jsx';
import { Badge, Card, Empty, ErrorState, Hash, Skeleton } from '../components/ui.jsx';

const TYPE_LABEL = { mint: 'Mint', check_in: 'Agent check-in', attest: 'Attestation', boost: 'Boost', vault: 'Vault', wallet_check_in: 'Check-in', wallet_map: 'Map', wallet_workflow: 'Workflow', wallet_deploy: 'Deploy' };

/**
 * Activity and proof history.
 *
 * A table on desktop and stacked records on narrow screens (§12.10): a
 * horizontally scrolling table of hashes is unreadable on a phone, and the
 * essential state must never require scrolling sideways to see.
 */
export function Activity() {
  const { sessionReady } = useWallet();
  const [filter, setFilter] = useState('all');
  const activity = useApi(() => Api.activity(), [sessionReady], { skip: !sessionReady });
  const runs = useApi(() => Api.runs(), [sessionReady], { skip: !sessionReady });

  if (activity.error) return <ErrorState error={activity.error} onRetry={activity.reload} context="Activity could not be loaded" />;
  if (!sessionReady || activity.loading || runs.loading || !activity.data || !runs.data) return <Skeleton rows={8} />;

  const rows = [
    ...activity.data.activity.map((t) => ({
      key: `tx-${t.id}`,
      kind: t.type,
      when: t.createdAt,
      txHash: t.txHash,
      status: t.status,
      blockNumber: t.blockNumber,
      explorerUrl: t.explorerUrl,
      runId: null,
      outcome: null,
      proofState: null,
    })),
    ...runs.data.runs.map((r) => ({
      key: `run-${r.id}`,
      kind: 'run',
      when: r.createdAt,
      txHash: null,
      status: null,
      runId: r.id,
      outcome: r.terminalOutcome,
      proofState: r.proofState,
      workflow: Boolean(r.workflowId),
    })),
  ]
    .filter((r) => filter === 'all' || r.kind === filter)
    .sort((a, b) => new Date(b.when) - new Date(a.when));

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <h1 className="page-title">Activity</h1>
          <p className="page-subtitle">Every run, transaction and proof for your account.</p>
        </div>
      </header>

      <div className="filters" role="group" aria-label="Filter activity">
        {['all', 'run', 'check_in', 'wallet_check_in', 'wallet_map', 'wallet_workflow', 'wallet_deploy'].map((f) => (
          <button
            key={f}
            type="button"
            className={`chip${filter === f ? ' chip--active' : ''}`}
            aria-pressed={filter === f}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : (TYPE_LABEL[f] ?? 'Runs')}
          </button>
        ))}
      </div>

      <Card>
        {rows.length === 0 ? (
          <Empty title="Nothing here yet" description="Runs, check-ins, maps, workflows and deploys will appear as they happen." />
        ) : (
          <>
            <table className="table table--desktop">
              <thead>
                <tr>
                  <th scope="col">Type</th>
                  <th scope="col">When</th>
                  <th scope="col">Outcome</th>
                  <th scope="col">Proof / status</th>
                  <th scope="col">Transaction</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key}>
                    <td>
                      {r.kind === 'run' ? 'Run' : TYPE_LABEL[r.kind] ?? r.kind}
                      {r.workflow && <Badge tone="neutral">scheduled</Badge>}
                    </td>
                    <td className="mono">{new Date(r.when).toLocaleString()}</td>
                    <td>{r.outcome ?? '—'}</td>
                    <td><StatusBadge row={r} /></td>
                    <td>
                      {r.txHash ? <Hash value={r.txHash} label="transaction" /> : r.runId ? <Link className="link" to={`/runs/${r.runId}`}>Open</Link> : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Stacked records for narrow screens. */}
            <ul className="records table--mobile">
              {rows.map((r) => (
                <li key={r.key} className="record">
                  <div className="record__head">
                    <span className="record__type">{r.kind === 'run' ? 'Run' : TYPE_LABEL[r.kind] ?? r.kind}</span>
                    <StatusBadge row={r} />
                  </div>
                  <p className="record__when mono">{new Date(r.when).toLocaleString()}</p>
                  {r.outcome && <p className="record__line">Outcome: {r.outcome}</p>}
                  {r.txHash && <p className="record__line"><Hash value={r.txHash} label="transaction" /></p>}
                  {r.runId && <Link className="link" to={`/runs/${r.runId}`}>Open run</Link>}
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>
    </div>
  );
}

function StatusBadge({ row }) {
  if (row.kind === 'run') {
    const confirmed = row.proofState === 'Confirmed and indexed';
    const failed = row.proofState === 'Proof failed';
    return <Badge tone={confirmed ? 'success' : failed ? 'danger' : 'pending'}>{confirmed ? 'Proof complete' : failed ? 'Proof failed' : 'Proof pending'}</Badge>;
  }
  const tone = row.status === 'INDEXED' ? 'success' : row.status === 'REVERTED' ? 'danger' : 'pending';
  return <Badge tone={tone}>{row.status ?? 'pending'}</Badge>;
}
